import { Grape, Scissors, Flame, Hammer, FlaskConical, Waves, Filter, Sparkles } from "lucide-react";
import KintsugiScrollVein from "../brand/KintsugiScrollVein";
import FleurKintsugi from "../brand/FleurKintsugi";
import Reveal from "./Reveal";

const ICONS = [Grape, Scissors, Flame, Hammer, FlaskConical, Waves, Filter];

export function parseSteps(raw) {
  return raw
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [title = "", ...rest] = l.split("|").map((s) => s.trim());
      return { title, text: rest.join(" | ") };
    });
}

function Step({ index, title, text }) {
  const Icon = ICONS[index] || Sparkles;
  return (
    <Reveal as="li" delay={index * 110} className="method-step" data-testid={`story-method-step-${index}`}>
      <div className="method-medallion" aria-hidden="true">
        <span className="font-display">{index + 1}</span>
      </div>
      <div className="method-step-body">
        <div className="flex items-center gap-3 mb-1.5">
          <Icon className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />
          <h3 className="font-display text-xl md:text-2xl tracking-wide">{title}</h3>
        </div>
        <p className="text-sm md:text-[0.95rem] text-muted-foreground leading-relaxed">{text}</p>
      </div>
    </Reveal>
  );
}

export default function StoryMethod({ eyebrow, title, intro, steps, tagline, image }) {
  const list = parseSteps(steps);
  if (list.length === 0) return null;
  return (
    <section className="relative overflow-hidden py-20 lg:py-28 kintsugi-bg" data-testid="story-method">
      <KintsugiScrollVein variant="horizontal" strokes={2} opacity={0.35} />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-3xl mb-14 lg:mb-20">
          <div className="smallcaps-label mb-4" data-testid="story-method-eyebrow">{eyebrow}</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-6" data-testid="story-method-title">
            <span className="gold-text">{title}</span>
          </h2>
          <p className="text-muted-foreground leading-relaxed text-base md:text-lg" data-testid="story-method-intro">{intro}</p>
        </Reveal>

        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-14 lg:gap-20 items-start">
          {image && (
            <Reveal delay={150} className="lg:sticky lg:top-28">
              <div className="kintsugi-frame aspect-[2/3] w-full max-w-md mx-auto" data-testid="story-method-poster">
                <img src={image} alt={title} loading="lazy" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          )}

          <div className="relative">
            <ol className="method-list">
              {list.map((s, i) => <Step key={i} index={i} title={s.title} text={s.text} />)}
            </ol>
            <Reveal delay={list.length * 110} className="mt-14 flex items-center gap-5" data-testid="story-method-tagline">
              <FleurKintsugi size={34} />
              <p className="font-vibes molten-flow leading-tight" style={{ fontSize: "clamp(1.9rem, 3vw, 2.6rem)" }}>{tagline}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
